import BarChartIcon from '@mui/icons-material/BarChart'
import EditIcon from '@mui/icons-material/Edit'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import {Box, CardActions, CardContent, CardHeader, Grid, Skeleton, Typography} from '@mui/material'
import {StrictMode, useCallback, useEffect, useState} from 'react'
import type {FC, ReactElement} from 'react'
import {useInView} from 'react-intersection-observer'
import {NavLink} from 'react-router-dom'
import D3DailyProductionBar from './D3DailyProductionBar.tsx'
import {useGetCockpitWarningsSignalBasedQuery} from '@apis/cockpit.ts'
import {useGetAvailabilityPerDayQuery} from '@apis/lineDashboard.ts'
import {filterWarnings} from '@app/util-functions.ts'
import ActionButton from '@containers//Dashboards/LinesOverview/components/ActionButton.tsx'
import CardWarning from '@containers//Dashboards/LinesOverview/shared/CardWarning.tsx'
import BasicCard from '@shared/components/BasicCard.tsx'
import LineStatusChip from '@shared/components/LineStatusChip.tsx'
import {useCustomTranslation} from '@shared/hooks/useCustomTranslation'
import {useEquipmentTypes} from '@shared/hooks/useEquipmentTypes.ts'
import {useResizeObserver} from '@shared/hooks/useResizeObserver'
import type {EquipmentInterface} from '@shared/interfaces/equipment.ts'
import {buildSignalExplorerUrl} from '@shared/utils/signalExplorerUrl'

interface EquipmentCardProps {
  equipment: EquipmentInterface
  showDetails?: boolean
  disconnectedSignalsCount?: number
}

const LabeledText = ({label, value}: {label: string; value: string}): ReactElement => (
  <Box sx={{alignItems: 'center'}}>
    <Typography variant="caption" color="text.disabled" component="span">
      {label}
    </Typography>
    <Typography variant="caption" color="text.primary" component="span" sx={{ml: 0.5}}>
      {value}
    </Typography>
  </Box>
)

const EquipmentCard: FC<EquipmentCardProps> = ({
  equipment,
  showDetails = true,
  disconnectedSignalsCount = 0,
}): ReactElement => {
  const {ref, inView} = useInView({triggerOnce: true})
  const [startDate, setStartDate] = useState<string>('')
  const [chartInnerWidth, setChartInnerWidth] = useState<number | null>(null)
  const [endDate] = useState<string>(new Date().toISOString())
  const summarizeBy = 'day'
  const margin = {
    top: 30,
    right: 30,
    bottom: 40,
    left: 40,
  }
  const {data: dailyAvailabilities, isLoading} = useGetAvailabilityPerDayQuery(
    {
      equipmentId: equipment.id,
      startDate,
      endDate,
      summarize_by: summarizeBy,
    },
    {skip: !inView},
  )

  const {data: cockpitWarnings} = useGetCockpitWarningsSignalBasedQuery(
    {equipmentId: equipment.id as string},
    {skip: !inView},
  )
  const [t] = useCustomTranslation('common')
  const [tLineManagement] = useCustomTranslation('line_management')
  const [tLinesOverview] = useCustomTranslation('lines_overview')
  const [tCockpit] = useCustomTranslation('cockpit')

  const {getEquipmentType} = useEquipmentTypes()

  useEffect(() => {
    const start = new Date()
    start.setDate(start.getDate() - 7)
    setStartDate(start.toISOString())
  }, [])

  const filteredWarnings = filterWarnings(cockpitWarnings || [])

  const handleGraphResize = useCallback(
    (entry: ResizeObserverEntry): void => {
      setChartInnerWidth(entry.contentRect.width - (margin.left + margin.right))
    },
    [margin.left, margin.right],
  )
  const equipmentCardGraphRef = useResizeObserver(handleGraphResize)

  return (
    <StrictMode>
      <BasicCard
        key={equipment.id}
        id={equipment.id}
        data-name="equipment-card"
        data-testid="mbai-equipments-overview-equipment-card-component"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        }}
      >
        <CardHeader
          sx={{pb: 0, borderBottom: 'none !important'}}
          title={
            <NavLink to={`/equipment/${equipment.id}`} style={{textDecoration: 'none'}}>
              <Typography
                variant="h3"
                data-name="equipment-title"
                data-cy="mbai-equipments-overview-equipment-card-title"
                sx={{
                  color: 'text.primary',
                  mb: 0,
                }}
              >
                {equipment.name}
              </Typography>
            </NavLink>
          }
          action={
            <Box sx={{display: 'flex'}}>
              {disconnectedSignalsCount > 0 ? (
                <NavLink
                  to={`/settings?sub_menu=machine_admin&tab=signals&connection_status=offline&equipmentIdFilter=${equipment.id}`}
                >
                  <LineStatusChip
                    connectivityStatus="Disconnected"
                    bold
                    tooltipText={tCockpit('disconnectedSignalsTooltip', {
                      count: disconnectedSignalsCount,
                    })}
                    displayLabel={false}
                  />
                </NavLink>
              ) : null}
              {filteredWarnings && filteredWarnings.length > 0 ? (
                <CardWarning warnings={filteredWarnings} equipmentId={equipment.id} />
              ) : null}
            </Box>
          }
        />
        <CardContent sx={{pb: '0 !important'}} ref={ref}>
          <Box
            sx={{
              height: '200px',
              backgroundColor: 'background.fadedOpaque',
            }}
            data-cy="mbai-line-card-image"
            ref={equipmentCardGraphRef}
          >
            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                }}
              >
                <Skeleton
                  variant="circular"
                  style={{
                    height: '150px',
                    width: '150px',
                  }}
                />
              </Box>
            ) : dailyAvailabilities && dailyAvailabilities && dailyAvailabilities.length > 0 ? (
              <D3DailyProductionBar
                data={dailyAvailabilities}
                width={chartInnerWidth as number}
                height={135}
                summarizeBy={summarizeBy}
                tooltip={false}
                margin={margin}
              />
            ) : (
              <Box
                sx={{
                  px: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '100%',
                  textAlign: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('availability_pie.no_data_available')}
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              pt: 1.5,
              pb: 0.5,
              display: 'flex',
              flexDirection: 'column',
              minHeight: '96px',
            }}
          >
            <LabeledText
              label={tLinesOverview('table_headers.line_name')}
              value={equipment.machine_name || ''}
            />
            {showDetails ? (
              <>
                <LabeledText
                  label={`${tLineManagement('equipment_management.type')}:`}
                  value={getEquipmentType(equipment.machine_sub_system_type as string)}
                />
                <LabeledText
                  label={`${t('programs.n_programs')}:`}
                  value={equipment.n_programs.toString()}
                />
                <LabeledText
                  label={`${t('programs.n_signals')}:`}
                  value={equipment.n_signals.toString()}
                />
              </>
            ) : null}
          </Box>
        </CardContent>
        <CardActions
          sx={{
            pt: '0',
            mt: 'auto',
            borderTop: 'none !important',
          }}
        >
          <Grid container spacing={2} sx={{flex: 1, minWidth: 0}}>
            <Grid size={4} sx={{minWidth: 0}}>
              <ActionButton
                title={tLineManagement('equipment_dashboard.view')}
                dataName="equipment-view-equipment-button"
                dataCy="mbai-equipments-overview-equipment-card-view-equipment-button"
                link={`/equipment/${equipment.id}`}
                icon={<BarChartIcon />}
              />
            </Grid>
            <Grid size={4} sx={{minWidth: 0}}>
              <ActionButton
                title={tLinesOverview('view_signals')}
                dataName="equipment-view-signals-button"
                dataCy="mbai-equipments-overview-equipment-card-view-signals-button"
                link={buildSignalExplorerUrl({
                  lineId: equipment.machine,
                  equipmentId: equipment.id,
                })}
                icon={<TrendingUpIcon />}
              />
            </Grid>
            <Grid size={4} sx={{minWidth: 0}}>
              <ActionButton
                title={tLineManagement('equipment_management.configure')}
                dataCy="mbai-equipments-overview-equipment-card-view-equipment-management-button"
                link={`/settings?sub_menu=machine_admin&tab=equipment_management&id=${equipment.id}`}
                icon={<EditIcon />}
              />
            </Grid>
          </Grid>
        </CardActions>
      </BasicCard>
    </StrictMode>
  )
}

export default EquipmentCard
